import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-[#012d5c] mb-4 pb-2 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const AccessibilityStatement = () => {
  useEffect(() => {
    document.title = "Accessibility Statement | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Integro Clinics is committed to making its website accessible. Read our Accessibility Statement to learn about our approach and how to report issues.";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Legal
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Accessibility Statement
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Integro Clinics is committed to making this website accessible to as many people as possible.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            <Section title="Our Commitment">
              <p>
                Integro Clinics is committed to ensuring that this website is accessible to all users, regardless of disability, device or browser. We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards, and we are continually working to improve the accessibility of our website.
              </p>
              <p>
                We recognise that accessibility is an ongoing process. Where our website does not yet fully meet WCAG 2.1 AA standards, we are actively working to identify and resolve issues.
              </p>
            </Section>

            <Section title="Technical Accessibility">
              <p>
                We have designed this website with accessibility in mind. Our approach includes:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Keyboard navigation: the website can be navigated without a mouse using standard keyboard controls.",
                  "Screen reader compatibility: we use semantic HTML elements and ARIA attributes to support screen reader users.",
                  "Alternative text: meaningful images include descriptive alt text.",
                  "Colour contrast: we aim to meet minimum contrast ratio requirements for text and interactive elements.",
                  "Responsive design: the website adapts to different screen sizes and devices.",
                  "Heading structure: pages use a logical heading hierarchy to aid navigation.",
                  "Focus indicators: interactive elements display visible focus states.",
                  "Form accessibility: form fields include appropriate labels to support assistive technology.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Known Limitations">
              <p>
                While we aim to meet WCAG 2.1 AA standards, we are aware that some areas of our website may not yet fully comply. Known limitations include:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Some older content may not include complete alt text descriptions.",
                  "Certain interactive components may not fully meet keyboard accessibility requirements.",
                  "Some multimedia content may not have accompanying transcripts.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                We are actively working to address these issues as part of our ongoing accessibility improvement programme.
              </p>
            </Section>

            <Section title="Assistive Technology">
              <p>
                This website has been designed to be compatible with a range of assistive technologies, including screen readers, voice recognition software and browser zoom functionality. We recommend using an up-to-date browser alongside current assistive technology for the best experience.
              </p>
              <p>
                If you need information from our website in an alternative format, please contact us using the details below and we will do our best to help.
              </p>
            </Section>

            <Section title="What We Are Doing to Improve Accessibility">
              <p>
                We are committed to making continuous improvements to the accessibility of this website. Our ongoing activities include:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Regular accessibility reviews of new and existing content.",
                  "Improving form labelling and error identification.",
                  "Reviewing and improving colour contrast across all pages.",
                  "Ensuring all interactive elements are keyboard accessible.",
                  "Adding captions and transcripts to multimedia content where applicable.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Reporting Accessibility Issues">
              <p>
                We welcome feedback about the accessibility of this website. If you experience any difficulty accessing any part of our site, or if you have suggestions for how we can improve accessibility, please contact us.
              </p>
              <p>
                We will acknowledge your report and aim to respond within a reasonable timeframe.
              </p>
            </Section>

            <Section title="Enforcement">
              <p>
                If you are not satisfied with our response to an accessibility concern, you may contact the Equality Advisory and Support Service (EASS). In England, Scotland and Wales, you can also contact the Equality and Human Rights Commission (EHRC).
              </p>
            </Section>

            <Section title="Preparation of This Statement">
              <p>
                This accessibility statement was prepared for the current Integro Clinics website. It reflects our commitment to accessibility and our ongoing work to improve it.
              </p>
            </Section>

            <Section title="Contact Us">
              <p>
                To report an accessibility issue or request content in an alternative format:
              </p>
              <div className="mt-4 bg-[#f8fafc] border border-gray-100 rounded-xl p-6 space-y-2">
                <p><strong>Email:</strong>{" "}
                  <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                    office@integroclinics.com
                  </a>
                </p>
              </div>
            </Section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccessibilityStatement;
